/**
 * Shared styles of the agent builder page (dashboard dark palette, as AgentPage / AgentHistoryPage).
 */
import styled, { keyframes } from 'styled-components';

export const Page = styled.div`
  height: 100%; overflow: hidden; background: #0f1419; color: #e6edf3;
  display: flex; flex-direction: column; padding: 20px 24px 16px;
  @media (max-width: 700px) { padding: 14px 12px; overflow-y: auto; height: auto; min-height: 100%; }
`;
export const Head = styled.div` display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; `;
export const Title = styled.h1` font-size: 22px; margin: 0; font-weight: 600; `;
export const Subtitle = styled.p` color: #8b98a5; margin: 4px 0 0; font-size: 13px; max-width: 760px; line-height: 1.5; `;
export const Layout = styled.div`
  flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(340px, 1fr); gap: 16px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; overflow-y: auto; }
`;
export const Card = styled.div`
  background: #161b22; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; min-width: 0; min-height: 0;
  display: flex; flex-direction: column;
`;
export const CardBody = styled.div` padding: 14px 16px; overflow-y: auto; flex: 1; min-height: 0; `;
export const CardTitle = styled.h3` margin: 0 0 10px; font-size: 14px; font-weight: 600; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; `;
export const Tabs = styled.div` display: flex; gap: 4px; padding: 8px 10px 0; border-bottom: 1px solid rgba(255,255,255,0.08); flex-wrap: wrap; `;
export const Tab = styled.button`
  background: ${p => (p.$on ? 'rgba(47,129,247,0.16)' : 'transparent')}; color: ${p => (p.$on ? '#e6edf3' : '#8b98a5')};
  border: none; border-bottom: 2px solid ${p => (p.$on ? '#2f81f7' : 'transparent')}; padding: 8px 12px; font-size: 13px; font-weight: 600; cursor: pointer; border-radius: 6px 6px 0 0;
  &:hover { color: #e6edf3; }
`;
export const Badge = styled.span` display: inline-block; min-width: 18px; padding: 0 6px; margin-left: 6px; border-radius: 999px; background: #2f81f7; color: #fff; font-size: 11px; line-height: 18px; text-align: center; `;
export const PrimaryBtn = styled.button`
  background: #2f81f7; color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  &:hover { background: #4f9bff; } &:disabled { opacity: .5; cursor: default; }
`;
export const GhostBtn = styled.button`
  background: transparent; color: #e6edf3; border: 1px solid rgba(255,255,255,0.14); border-radius: 8px; padding: 7px 12px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  &:hover { background: rgba(255,255,255,0.06); } &:disabled { opacity: .5; cursor: default; }
`;
export const SmallBtn = styled(GhostBtn)` padding: 4px 9px; font-size: 12px; `;
export const DangerBtn = styled(SmallBtn)` color: #f85149; border-color: rgba(248,81,73,0.4); `;
export const OkBtn = styled(SmallBtn)` color: #56d364; border-color: rgba(63,185,80,0.45); `;
export const Row = styled.div` display: flex; gap: 8px; flex-wrap: wrap; align-items: center; `;
export const Muted = styled.span` color: #8b98a5; font-size: 12px; `;
export const Help = styled.div` color: #8b98a5; font-size: 12px; line-height: 1.5; margin-top: 6px; `;
export const Pill = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; white-space: nowrap;
  background: ${p => p.$bg || 'rgba(255,255,255,0.08)'}; color: ${p => p.$fg || '#e6edf3'};
`;
export const Input = styled.input`
  width: 100%; background: #0f1419; border: 1px solid rgba(255,255,255,0.12); color: #e6edf3; border-radius: 8px; padding: 9px 11px; font-size: 13px; outline: none;
  &:focus { border-color: #2f81f7; }
`;
export const TextArea = styled.textarea`
  width: 100%; background: #0f1419; border: 1px solid rgba(255,255,255,0.12); color: #e6edf3; border-radius: 8px; padding: 9px 11px; font-size: 13px; line-height: 1.5; outline: none; resize: vertical;
  font-family: ${p => (p.$mono ? 'ui-monospace, Menlo, Consolas, monospace' : 'inherit')};
  &:focus { border-color: #2f81f7; }
`;
export const Select = styled.select` background: #0f1419; border: 1px solid rgba(255,255,255,0.12); color: #e6edf3; border-radius: 8px; padding: 7px 10px; font-size: 13px; `;
export const Bar = styled.div` height: 6px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; width: ${p => p.$w || '100%'}; > div { height: 100%; width: ${p => p.$pct}%; background: ${p => (p.$pct >= 75 ? '#3fb950' : p.$pct >= 40 ? '#d29922' : '#2f81f7')}; transition: width .4s; } `;
export const Empty = styled.div` color: #8b98a5; font-size: 13px; text-align: center; padding: 28px 12px; line-height: 1.6; `;
export const Box = styled.div` border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 10px 12px; margin-bottom: 10px; background: #0f1419; `;
export const Pre = styled.pre` white-space: pre-wrap; word-break: break-word; font-size: 11px; line-height: 1.45; color: #c9d1d9; background: #0b0f14; border-radius: 8px; padding: 10px; max-height: 320px; overflow: auto; margin: 8px 0 0; `;

const pulse = keyframes` 0%,100% { transform: scaleY(.35); } 50% { transform: scaleY(1); } `;
export const Wave = styled.div`
  display: flex; align-items: center; gap: 3px; height: 26px;
  span { width: 3px; border-radius: 2px; background: ${p => (p.$phase === 'speaking' ? '#56d364' : '#2f81f7')}; height: 100%; animation: ${pulse} ${p => (p.$phase === 'listening' ? '1.4s' : '0.8s')} ease-in-out infinite; opacity: ${p => (p.$phase === 'thinking' ? 0.45 : 1)}; }
`;

export const fmtWhen = (d) => (d ? new Date(d).toLocaleString() : '');
export const errText = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;

export const KIND_LABEL = {
  pathway: 'Route', knowledgeCategory: 'Topic', strategy: 'Strategy', ruleSet: 'Rule', teaching: 'Correction',
  directive: 'Instructions', pipeline: 'Case pipeline', watchFact: 'Live-check fact', storefront: 'Storefront',
  interviewQuestions: 'Interview questions', questionLimit: 'Question limit', greeting: 'Greeting', interview: 'Interview',
};
export const OP_LABEL = { create: ['Add', 'rgba(63,185,80,0.18)', '#56d364'], update: ['Change', 'rgba(47,129,247,0.18)', '#79c0ff'], delete: ['Remove', 'rgba(248,81,73,0.18)', '#ff7b72'] };
export const STATUS_LABEL = {
  proposed: ['Waiting for you', 'rgba(210,153,34,0.18)', '#e3b341'],
  applied: ['In your agent', 'rgba(63,185,80,0.18)', '#56d364'],
  rejected: ['Dismissed', 'rgba(139,152,165,0.18)', '#8b98a5'],
  conflict: ['Changed meanwhile', 'rgba(248,81,73,0.18)', '#ff7b72'],
  failed: ['Failed', 'rgba(248,81,73,0.18)', '#ff7b72'],
  superseded: ['Replaced by newer', 'rgba(139,152,165,0.18)', '#8b98a5'],
  undone: ['Undone', 'rgba(139,152,165,0.18)', '#8b98a5'],
};
